import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Gift, Sparkles } from "lucide-react";

/**
 * Reusable points slider — emits {points, discount} via onChange.
 * - Conversion: 10 points = $1
 * - Discount capped at 50% of original amount.
 *
 * Props:
 *   amount: number  — the price the user is paying (USD)
 *   onChange(points, discount): callback fired whenever the slider moves
 */
export default function PointsRedeemer({ amount, onChange, testId = "points-redeemer" }) {
  const [available, setAvailable] = useState(0);
  const [points, setPoints] = useState(0);

  useEffect(() => {
    api.get("/referrals/my").then((r) => setAvailable(r.data.points || 0)).catch(() => {});
  }, []);

  // Cap: max points usable for this amount = min(available, floor(amount * 0.5 * 10))
  const maxByAmount = Math.floor(amount * 0.5 * 10);
  const max = Math.max(0, Math.min(available, maxByAmount));
  const discount = Math.round((points / 10) * 100) / 100;

  useEffect(() => {
    onChange?.(points, discount);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points]);

  // Reset points if max shrinks below current
  useEffect(() => {
    if (points > max) setPoints(max);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [max]);

  if (available <= 0) {
    return (
      <div className="rounded-md border border-[#E2E8F0] bg-slate-50 px-3 py-2 text-xs text-[#475569] flex items-center gap-2" data-testid={`${testId}-empty`}>
        <Gift className="w-3.5 h-3.5" /> Earn points by referring friends
      </div>
    );
  }

  return (
    <div className="rounded-md border border-[#C9A356]/40 bg-[#FFF8E7] p-3" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm text-[#0A2540] font-medium">
          <Sparkles className="w-4 h-4 text-[#C9A356]" /> Use referral points
        </div>
        <div className="text-xs text-[#475569]" data-testid={`${testId}-balance`}>
          Balance: <span className="font-mono font-bold text-[#0A2540]">{available}</span>
        </div>
      </div>
      <input
        type="range"
        min="0"
        max={max}
        step="10"
        value={points}
        onChange={(e) => setPoints(parseInt(e.target.value, 10))}
        data-testid={`${testId}-slider`}
        className="w-full accent-[#C9A356]"
        disabled={max === 0}
      />
      <div className="flex justify-between items-center mt-1.5 text-xs">
        <span className="text-[#475569]">
          Redeem <span className="font-bold text-[#0A2540]" data-testid={`${testId}-points`}>{points}</span> pts
        </span>
        <span className="font-bold text-emerald-600" data-testid={`${testId}-discount`}>
          –${discount.toFixed(2)}
        </span>
      </div>
      {max < available && (
        <p className="text-[10px] text-[#94A3B8] mt-1">Capped at 50% of order ({maxByAmount} pts)</p>
      )}
    </div>
  );
}
